import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Copy, Download, Loader2, RefreshCw, Scissors } from "lucide-react";
import { API, http, errMsg, downloadFile } from "../lib/api";

const LEN = [["20", "20 sec"], ["40", "40 sec"], ["60", "1 min"]];
const copy = (t, what) => navigator.clipboard.writeText(t).then(() => toast.success(`${what} copied`));
const CopyBtn = ({ text, what, testid }) => (
  <button data-testid={testid} onClick={() => copy(text, what)} className="inline-flex items-center gap-1 rounded-sm border border-stone-300 bg-white px-2 py-1 text-xs hover:bg-stone-100 transition-colors"><Copy size={12} /> Copy</button>
);

const tags = (a) => (a || []).map((h) => `#${String(h).replace(/^#/, "").replace(/\s+/g, "")}`).join(" ");
const PLATFORMS = [
  ["instagram", "Instagram Reels", (c) => `${c.caption}\n\n${c.cta || ""}\n\n${tags(c.hashtags)}`.trim()],
  ["youtube", "YouTube Shorts", (c) => `Title: ${c.title}\n\n${c.description}\n\nTags: ${(c.tags || []).join(", ")}`],
  ["facebook", "Facebook", (c) => `${c.post}\n\n${c.link_prompt || ""}`.trim()],
  ["tiktok", "TikTok", (c) => `${c.caption}\n\n${tags(c.hashtags)}`.trim()],
];
const captionsOf = (v) => v.captions || { instagram: { caption: v.caption, hashtags: v.hashtags, cta: v.cta } };

const Captions = ({ v }) => {
  const c = captionsOf(v);
  return (
    <div data-testid="vp-captions" className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
      {PLATFORMS.filter(([k]) => c[k]).map(([k, l, fmt]) => (
        <div key={k} data-testid={`vp-caption-${k}`} className="rounded-md bg-stone-50 p-3">
          <div className="flex items-center justify-between mb-1"><b>{l}</b><CopyBtn text={fmt(c[k])} what={`${l} caption`} testid={`vp-copy-caption-${k}`} /></div>
          <p className="whitespace-pre-line text-stone-700">{fmt(c[k])}</p>
        </div>
      ))}
      {!v.captions && <p className="text-xs text-amber-700 md:col-span-2">This pack was made before platform captions — press Recreate to get YouTube, Facebook and TikTok versions.</p>}
    </div>
  );
};

const allText = (v, ar) => [
  `${v.title} — ${ar === "9x16" ? "vertical 9:16" : "horizontal 16:9"}`,
  ...v.shots.map((s, i) => `Shot ${i + 1} (${s.start}–${s.end}s)\n${s.image ? `Image: Shot-${String(i + 1).padStart(2, "0")}.jpg\n` : ""}Description: ${s.image && s.prompt_animate ? s.prompt_animate : s[`prompt_${ar}`]}\nOn screen: ${s.on_screen_text}`),
  `Voiceover script:\n${v.voiceover_full}`,
  ...PLATFORMS.filter(([k]) => captionsOf(v)[k]).map(([k, l, fmt]) => `${l} caption:\n${fmt(captionsOf(v)[k])}`),
].join("\n\n");

const WPS = 2.5;
const ScriptFit = ({ text, secs, trim }) => {
  const [busy, setBusy] = useState(false);
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  const read = Math.round(words / WPS);
  const max = Math.floor(secs * WPS);
  const ok = words <= max;
  const run = async () => {
    setBusy(true);
    try {
      const r = await trim();
      if (r.fits) toast.success(`Script trimmed to ${r.words} words — fits in ${secs} sec`);
      else toast.warning(`Trimmed to ${r.words} words — still a little long, press Trim again`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="vp-script-fit" className="flex flex-wrap items-center gap-2 mb-2 text-xs">
      <span data-testid="vp-script-words" className="text-stone-600">{words} words · about {read} sec read aloud</span>
      <span data-testid="vp-script-fit-badge" className={`rounded-sm px-1.5 py-0.5 border ${ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
        {ok ? `Fits in ${secs} sec` : `Too long for ${secs} sec — cut about ${words - max} words`}
      </span>
      {!ok && (
        <button data-testid="vp-script-trim" onClick={run} disabled={busy}
          className="inline-flex items-center gap-1 rounded-sm bg-[#2B2A26] px-2 py-0.5 font-bold text-white hover:bg-black transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Scissors size={11} />} {busy ? "Trimming…" : `Trim to fit ${secs} sec`}
        </button>
      )}
    </div>
  );
};

const imgUrl = (src, ref, ar, thumb) => `${API}/video-prompts/${src.kind}/${src.id}/image?ref=${encodeURIComponent(ref)}&ar=${ar}${thumb ? "&thumb=1" : ""}`;
const save = (p, name) => downloadFile(p, name).catch((e) => toast.error(errMsg(e)));

const ShotImage = ({ s, i, ar, src }) => s.image ? (
  <div className="space-y-1">
    <img data-testid={`vp-shot-img-${i}`} src={imgUrl(src, s.image, ar, true)} alt={`Shot ${i + 1} start`} loading="lazy"
      className={`${ar === "9x16" ? "h-32 w-[72px]" : "h-[72px] w-32"} rounded-sm object-cover border border-stone-200 bg-stone-100`} />
    <button data-testid={`vp-shot-img-download-${i}`} onClick={() => save(`/video-prompts/${src.kind}/${src.id}/image?ref=${encodeURIComponent(s.image)}&ar=${ar}`, `Shot-${i + 1}.jpg`)}
      className="inline-flex items-center gap-1 rounded-sm border border-stone-300 bg-white px-2 py-1 text-xs hover:bg-stone-100 transition-colors"><Download size={12} /> Image</button>
  </div>
) : <div data-testid={`vp-shot-noimg-${i}`} className="text-[11px] text-stone-400">{s.prompt_animate !== undefined ? "Text only — no image" : ""}</div>;

const Shot = ({ s, i, ar, src }) => {
  const main = s.image && s.prompt_animate ? s.prompt_animate : s[`prompt_${ar}`];
  return (
    <li data-testid={`vp-shot-${i}`} className="border-t border-stone-100 py-3 grid md:grid-cols-12 gap-3">
      <div className="md:col-span-1 text-xs font-mono-ui text-stone-500">Shot {i + 1}<br />{s.start}–{s.end}s</div>
      <div className="md:col-span-2"><ShotImage s={s} i={i} ar={ar} src={src} /></div>
      <div className="md:col-span-6 text-sm text-[#2B2A26] leading-relaxed">
        <div className="text-[11px] uppercase tracking-wider text-[#9A7B2C] mb-1">Description — paste into Higgsfield</div>
        <p data-testid={`vp-shot-prompt-${i}`}>{main}</p>
      </div>
      <div className="md:col-span-3 text-xs space-y-2">
        <div data-testid={`vp-shot-onscreen-${i}`}><b>On screen:</b> {s.on_screen_text}</div>
        <CopyBtn text={main} what={`Shot ${i + 1} description`} testid={`vp-copy-shot-${i}`} />
      </div>
    </li>
  );
};

const Version = ({ v, ar, src, len, trim }) => (
  <div data-testid="vp-version">
    <div className="flex flex-wrap items-center gap-3 mb-2">
      <b className="text-[#2B2A26]">{v.title}</b>
      <span className="ml-auto flex gap-2">
        {v.shots.some((s) => s.image) && <button data-testid="vp-download-zip" onClick={() => save(`/video-prompts/${src.kind}/${src.id}/zip?length=${len}&ar=${ar}`, `video-${len}s-${ar}-shots.zip`)}
          className="inline-flex items-center gap-1 rounded-sm bg-[#C9A847] px-2 py-1 text-xs font-bold text-white hover:opacity-90 transition-opacity"><Download size={12} /> All shot images (zip)</button>}
        <CopyBtn text={allText(v, ar)} what="Whole video pack" testid="vp-copy-all" />
      </span>
    </div>
    {v.shots[0]?.prompt_animate === undefined && <p data-testid="vp-old-pack" className="text-xs text-amber-700">This pack was made before shot images — press Recreate to get a picture for each shot.</p>}
    <ul>{v.shots.map((s, i) => <Shot key={i} s={s} i={i} ar={ar} src={src} />)}</ul>
    <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
      <div className="rounded-md bg-stone-50 p-3"><div className="flex items-center justify-between mb-1"><b>Voiceover script</b><CopyBtn text={v.voiceover_full} what="Voiceover" testid="vp-copy-vo" /></div><ScriptFit text={v.voiceover_full} secs={Number(len)} trim={trim} /><p className="whitespace-pre-line text-stone-700">{v.voiceover_full}</p></div>
    </div>
    <Captions v={v} />
  </div>
);

export const VideoPrompts = ({ data, path, reload }) => {
  const [len, setLen] = useState("20");
  const [ar, setAr] = useState("9x16");
  const st = data.video_prompts_status || {};
  const vp = data.video_prompts;
  const running = st.state === "running";
  useEffect(() => { if (!running) return undefined; const t = setInterval(reload, 5000); return () => clearInterval(t); }, [running, reload]);
  const go = async () => {
    try { await http.post(path); toast.message("Writing 20 s, 40 s and 1 min video prompts — about a minute"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const v = vp?.lengths?.[len];
  return (
    <div data-testid="video-prompts" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Clapperboard size={18} className="text-[#C9A847]" /><b className="text-[#2B2A26]">Higgsfield video prompts</b>
        {vp && <span className="text-xs text-stone-500">Made {new Date(vp.at).toLocaleString("en-AU")}</span>}
        <button data-testid="vp-create" onClick={go} disabled={running}
          className="ml-auto inline-flex items-center gap-2 rounded-sm bg-[#2B2A26] px-4 py-2 text-sm font-bold text-white hover:bg-black transition-colors disabled:opacity-40">
          {running ? <Loader2 size={15} className="animate-spin" /> : vp ? <RefreshCw size={15} /> : <Clapperboard size={15} />} {running ? "Writing…" : vp ? "Recreate video prompts" : "Create video prompts"}
        </button>
      </div>
      {st.state === "failed" && <p data-testid="vp-error" className="text-sm text-red-700">Couldn't write them: {st.error}</p>}
      {st.missing?.length > 0 && <p className="text-xs text-amber-700">Missing: {st.missing.map((m) => LEN.find((l) => l[0] === m)?.[1]).join(", ")} — press Recreate to try again.</p>}
      {vp && (<>
        <div className="flex flex-wrap gap-2">
          {LEN.map(([k, l]) => <button key={k} data-testid={`vp-len-${k}`} onClick={() => setLen(k)} disabled={!vp.lengths[k]} className={`rounded-full px-4 py-1.5 text-sm border transition-colors disabled:opacity-30 ${len === k ? "bg-[#2B2A26] text-white border-[#2B2A26]" : "bg-white border-stone-300 hover:border-stone-500"}`}>{l}</button>)}
          <span className="w-4" />
          {[["9x16", "Vertical 9:16 · Reels, Shorts, Facebook"], ["16x9", "Horizontal 16:9 · YouTube"]].map(([k, l]) => <button key={k} data-testid={`vp-ar-${k}`} onClick={() => setAr(k)} className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${ar === k ? "bg-[#C9A847] text-white border-[#C9A847]" : "bg-white border-stone-300 hover:border-stone-500"}`}>{l}</button>)}
        </div>
        {v ? <Version v={v} ar={ar} len={len} trim={async () => { const r = await http.post(`${path}/trim`, { length: len }); await reload(); return r.data; }} src={{ kind: path.startsWith("/kits") ? "kit" : "extra", id: data.id }} /> : <p className="text-sm text-stone-500">This length isn't ready — press Recreate.</p>}
      </>)}
      {!vp && !running && <p className="text-sm text-stone-500">Creates a shot-by-shot Higgsfield prompt pack for a 20 second, 40 second and 1 minute sales video, each in vertical and YouTube shape, with voiceover, on-screen text and separate Instagram, YouTube, Facebook and TikTok captions.</p>}
    </div>
  );
};
